import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Label } from '@radix-ui/react-label';
import { cn } from '@utils/utils';

export interface DateRangePickerProps {
  startDate?: string;
  endDate?: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
  error?: string;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  showTimeSelect?: boolean;
}

const parseDateTime = (dateTimeString?: string): Date | null => {
  if (!dateTimeString) return null;
  
  // Si es solo fecha (YYYY-MM-DD), crear la fecha en zona horaria local
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateTimeString)) {
    const [year, month, day] = dateTimeString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  
  // Si incluye tiempo (YYYY-MM-DD HH:mm), crear la fecha en zona horaria local
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(dateTimeString)) {
    const [datePart, timePart] = dateTimeString.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  }
  
  // Para otros formatos, usar el constructor por defecto
  return new Date(dateTimeString);
};

const formatDateTime = (date: Date | null): string => {
  if (!date) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const formatDateOnly = (date: Date | null): string => {
  if (!date) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const DateRangePicker = React.forwardRef<HTMLDivElement, DateRangePickerProps>(
  ({ 
    startDate, 
    endDate, 
    onStartDateChange, 
    onEndDateChange, 
    disabled, 
    label, 
    error, 
    className, 
    minDate, 
    maxDate,
    showTimeSelect = false
  }, ref) => {
    const startDateParsed = parseDateTime(startDate);
    const endDateParsed = parseDateTime(endDate);

    const handleDateChange = (dates: [Date | null, Date | null]) => {
      const [start, end] = dates;
      
      if (start) {
        onStartDateChange(showTimeSelect ? formatDateTime(start) : formatDateOnly(start));
      }
      
      if (end) {
        onEndDateChange(showTimeSelect ? formatDateTime(end) : formatDateOnly(end));
      }
    };

    const formatDisplayValue = () => {
      if (startDateParsed && endDateParsed) {
        const startStr = showTimeSelect 
          ? startDateParsed.toLocaleDateString('es-ES') + ' ' + startDateParsed.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
          : startDateParsed.toLocaleDateString('es-ES');
        const endStr = showTimeSelect 
          ? endDateParsed.toLocaleDateString('es-ES') + ' ' + endDateParsed.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
          : endDateParsed.toLocaleDateString('es-ES');
        return `${startStr} - ${endStr}`;
      } else if (startDateParsed) {
        const startStr = showTimeSelect 
          ? startDateParsed.toLocaleDateString('es-ES') + ' ' + startDateParsed.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
          : startDateParsed.toLocaleDateString('es-ES');
        return `${startStr} - Selecciona fecha fin`;
      }
      return 'Selecciona un rango de fechas';
    };

    return (
      <div className={cn("space-y-4", className)} ref={ref}>
        {label && (
          <Label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            {label}
          </Label>
        )}
        
        {/* Mostrar el rango seleccionado */}
        {(startDateParsed || endDateParsed) && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium">
              <span className="text-blue-600">Rango seleccionado:</span> {formatDisplayValue()}
            </p>
          </div>
        )}

        {/* Calendario siempre visible */}
        <div className="bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden">
          <div className="text-xs text-gray-500 flex items-center p-2 text-center">
            💡 Haz clic en una fecha para iniciar el rango, luego en otra para completarlo
          </div>
          <div className="flex justify-center">
            <DatePicker
              selected={startDateParsed}
              onChange={handleDateChange}
              startDate={startDateParsed}
              endDate={endDateParsed}
              selectsRange
              inline
              showTimeSelect={showTimeSelect}
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="yyyy-MM-dd"
              minDate={minDate}
              maxDate={maxDate}
              disabled={disabled}
              locale="es"
              calendarClassName="border-0"
            />
          </div>
          
          {/* Botones de acción */}
          <div className="flex justify-between p-3 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={() => {
                onStartDateChange('');
                onEndDateChange('');
              }}
              className="w-full     px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors"
            >
              Limpiar selección
            </button>
            
          </div>
        </div>
        
        {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
      </div>
    );
  }
);

DateRangePicker.displayName = "DateRangePicker";

export { DateRangePicker };
